import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Keyboard, Moon, Palette, ScanEye, Sun, Zap } from 'lucide-react';
import { DEVICE_PIXEL_RATIO } from 'ol/has';
import { useColorStore } from '../../stores/colorStore';
import { useViewerSharpnessSetting } from './viewerSharpness';
import { ShortcutsDialog } from './ShortcutsDialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * The viewer's bottom bar (decided 2026-09-10): the theme switch, the same
 * choice Settings offers, and the slide's color management when the slide
 * carries an ICC profile. Drawn in the community tokens, so it follows the
 * theme it switches. Both editions' (2026-10-04: the viewer is the same page
 * in both); the enterprise status bar leaves the study page to it.
 *
 * On the left, TileRiver and its mark, a way home (user, 2026-09-26): in the
 * bar's ink, the mark turning the logo's teal on hover. Where home is, the
 * shell says (`home`): the community's front page, the enterprise worklist.
 * The color profile sits on the right, beside the theme switch.
 *
 * Shortcuts, the same reference the enterprise status bar opens (user,
 * 2026-10-01: in both editions), before the color profile. Not on a device
 * without hover (a phone, a tablet): it has no keyboard to use them on.
 *
 * Speed or Sharpness (user, 2026-10-11), before the theme, the same choice
 * Settings offers, kept on the account when signed in: which pixels the
 * slide's pyramid level is chosen by (viewer/sharpness.ts), speed by default.
 * Only on a screen of more than one pixel per layout pixel (a Retina
 * display): on any other the two draw the same, so the choice is not shown.
 * The density is the one OpenLayers renders at, read when the page loaded.
 * Sharpness sharpens once the view is still; Sharpness in Motion, which keeps
 * it sharp while moving too, is Settings' alone, a step further away (user,
 * 2026-10-12). Movement (Responsive or Smooth, viewer/glide.ts) is Settings'
 * alone too: the two differ only at the end of a flick, not worth a place in
 * the bar (user, 2026-10-11).
 */
/** The bar's plain buttons: Shortcuts and the color profile. */
const BAR_BUTTON: CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, height: 22, padding: '0 7px', border: 0, borderRadius: 5, background: 'transparent', color: 'var(--tr-muted)', fontSize: 12, fontFamily: 'inherit', cursor: 'pointer' };

export function ViewerBottomBar({
  light,
  onChange,
  home,
  screenDensity = DEVICE_PIXEL_RATIO,
}: {
  light: boolean;
  onChange: (light: boolean) => void;
  home: { to: string; label: string };
  /** Device pixels per layout pixel of the screen the viewer draws on. */
  screenDensity?: number;
}) {
  const available = useColorStore((s) => s.available);
  const enabled = useColorStore((s) => s.enabled);
  const name = useColorStore((s) => s.name);
  const loading = useColorStore((s) => s.nameLoading);
  const toggle = useColorStore((s) => s.toggle);
  const unavailable = useColorStore((s) => s.unavailable);
  const [sharp, setSharp] = useViewerSharpnessSetting();
  // One pixel per layout pixel: sharpness would draw the same as speed.
  const denseScreen = screenDensity > 1;
  const option = (on: boolean, label: string, icon: ReactNode, pick: () => void) => (
    <button
      type="button"
      aria-pressed={on}
      onClick={pick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 5,
        height: 20,
        padding: '0 8px',
        border: 0,
        borderRadius: 5,
        background: on ? 'var(--tr-surface)' : 'transparent',
        color: on ? 'var(--tr-ink)' : 'var(--tr-muted)',
        boxShadow: on ? 'var(--tr-shadow-raised)' : 'none',
        fontSize: 12,
        fontWeight: 500,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {icon}
      {label}
    </button>
  );
  const profile = available && toggle ? (
    <button
      type="button"
      onClick={toggle}
      className="tr-hover-track-ink"
      title={enabled ? `Color correction is on (${name ?? 'embedded profile'}). Click to show the raw color.` : 'Showing the raw color. Click to turn color correction on.'}
      style={BAR_BUTTON}
    >
      <Palette size={13} style={{ opacity: enabled ? 1 : 0.6 }} />
      <span style={{ opacity: enabled ? 1 : 0.6 }}>{enabled ? (loading ? 'Color profile…' : name ?? 'Color corrected') : 'Raw color'}</span>
    </button>
  ) : unavailable ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, height: 22, padding: '0 7px', fontSize: 12, opacity: 0.6 }}>
          <Palette size={13} />
          Raw color
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">
        This slide carries a color profile{name ? ` (${name})` : ''} that could not be applied, so it is shown in its raw color.
      </TooltipContent>
    </Tooltip>
  ) : null;
  const detail = (on: boolean, label: string, icon: ReactNode, pick: () => void, hint: string) => (
    <Tooltip>
      <TooltipTrigger asChild>{option(on, label, icon, pick)}</TooltipTrigger>
      <TooltipContent side="top" style={{ maxWidth: 260 }}>
        {hint}
      </TooltipContent>
    </Tooltip>
  );
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        height: 32,
        padding: '0 20px',
        flex: 'none',
        borderTop: '1px solid var(--tr-line)',
        background: 'var(--tr-page)',
        color: 'var(--tr-muted)',
        fontSize: 12.5,
        fontFamily: "'Instrument Sans', system-ui, sans-serif",
      }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Link to={home.to} className="tr-viewer-home">
            <TileRiverMark height={14} />
            <span>TileRiver</span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="top">{home.label}</TooltipContent>
      </Tooltip>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <ShortcutsDialog>
          <button
            type="button"
            className="tr-hover-track-ink tr-viewer-shortcuts"
            style={BAR_BUTTON}
          >
            <Keyboard size={13} />
            Shortcuts
          </button>
        </ShortcutsDialog>
        {profile}
        {denseScreen && (
          <div role="group" aria-label="Speed or sharpness" style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 2, borderRadius: 7, background: 'var(--tr-track)' }}>
            {detail(!sharp, 'Speed', <Zap size={12} />, () => setSharp(false), 'The image fills in faster as you move around it.')}
            {detail(sharp, 'Sharpness', <ScanEye size={12} />, () => setSharp(true), 'Finer detail once the image stops moving. Takes longer to fill in on a slow connection.')}
          </div>
        )}
        <div role="group" aria-label="Viewer theme" style={{ display: 'flex', alignItems: 'center', gap: 2, padding: 2, borderRadius: 7, background: 'var(--tr-track)' }}>
          {detail(!light, 'Dark', <Moon size={12} />, () => onChange(false), 'Dark theme')}
          {detail(light, 'Light', <Sun size={12} />, () => onChange(true), 'Light theme')}
        </div>
      </div>
    </div>
  );
}

/**
 * The logo's mark (public/brand/logo-icon.svg), drawn in the current color so
 * the bar can show it in its own ink and turn it the logo's teal on hover.
 */
function TileRiverMark({ height }: { height: number }) {
  return (
    <svg className="tr-viewer-home-mark" viewBox="0 0 92 83" height={height} width={(height * 92) / 83} fill="currentColor" aria-hidden style={{ display: 'block', flex: 'none' }}>
      <g transform="matrix(1,0,0,1,-27.9,-38.653946)">
        <g transform="matrix(1,0,0,1,-109,-251)">
          <g transform="matrix(1.040065,0,0,1.040065,-8.605075,-16.147186)">
            <g transform="matrix(1.23796,0,0,1.23796,-56.811908,-82.776158)">
              <path d="M173.9,337.8C171.6,338.5 165.6,338.9 164.2,336.6C163,334.5 163,329.2 164.2,327.1C165.5,325.3 168,325.5 170.5,325.4C173.6,325.4 176,326 176.2,329.6C176.3,332.2 176.9,336.7 174,337.8L173.9,337.8Z" />
              <path d="M229,332.4C228.1,331.8 226.5,331.7 224.3,331.7C220.3,331.7 215.2,331.8 211.2,331.6C209.7,331.5 208.3,331 207.8,329.5C206.8,327 207.5,321.9 206.3,319.8C205,317.5 201.8,318.2 199.4,318.1L189.6,318.1C186.1,318.1 184.3,317.8 183.8,314.3C183.1,311.3 184.5,305.2 180.5,304.6C175.9,304.1 169.2,304.6 164.5,304.4C163.1,304.4 161.5,304.4 160.4,305.2C159.7,305.7 159.3,306.8 159.2,307.7C158.9,309.4 158.9,311.1 158.9,312.9C159,315.3 159,317 160.1,318.4C160.9,319.2 162.3,319.5 164.3,319.5L174.4,319.5C181.1,319.4 182.3,319.8 182.4,327C182.6,332.7 184,333.4 189.3,333.3L199.3,333.3C201.4,333.3 203.1,333.4 204,334.1C207.2,336.7 204.5,343.2 207.2,346.1C208.5,347.1 210.5,347 212.1,347L224,347C226.1,347 228.9,347.2 229.7,344.9C230.4,343.1 231,334.3 228.8,332.7L229,332.4Z" />
              <path d="M222.4,353.6C221.1,353 219.5,353 218,353L206.6,353C204.9,353 202.6,353.1 201.6,351.4C200.2,348.9 201.3,343.4 199.8,340.8C198.4,338.8 195.9,339.4 192.7,339.2L185.9,339.2C181.7,339.2 178.8,339 177.5,340.1C175.5,342 176,348.6 176.4,351.2C176.6,352.2 176.9,353 177.7,353.5C179,354.4 181.8,354.3 185.5,354.3C189,354.5 194.5,353.9 197.5,354.8C201.2,356.7 198.3,364 200.9,367C202.1,368.3 204.7,368 208.7,368.1L213.2,368.1C216.5,367.9 222.1,368.9 223.4,366.6C224.5,364.4 224.2,361.7 224.3,359.3C224.3,357.4 224.1,354.3 222.5,353.5L222.4,353.6Z" />
            </g>
          </g>
        </g>
      </g>
    </svg>
  );
}
